<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elavation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to Settings" @click="$router.go(-1)">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <help-link appId="settings" pageId="leave-types" color="white"></help-link> 
                    <v-btn v-if="hasEditRights" fab dark small color="primary" title="Add New Leave Type" @click="showAdd">
                        <v-icon dark>add</v-icon>
                    </v-btn>
                </v-toolbar>
            </v-card>
            <v-alert v-if="error" :value="true" color="error" icon="error" outline class="mt-5">
                Error encountered. Please contact support. Error details: {{errorMessage}}
            </v-alert> 

            <v-card class="elavation-1 mt-5"> 
                <v-toolbar flat height="45px">
                    <div :class="isMobile?'wp-50 mb-10':'wp-30 mb-10'">
                        <v-text-field v-model="search" append-icon="search" label="Search" single-line hide-details></v-text-field>
                    </div>
                    <v-spacer></v-spacer>
                    <span class="caption">
                        <v-switch
                            v-model="userDefined"
                            label="User Defined"
                            color="primary"
                            class="mt-20"
                        ></v-switch> 
                    </span>
                </v-toolbar>
                <v-data-table
                    :headers="isMobile?headers_mobile:headers"
                    :items="leave_types_filtered"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                >
                <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
                    <template v-slot:items="props">
                        <tr v-if="props.item.created.userId==0" title="System generated. Not editable.">
                            <td>{{ props.item.leaveCode }}</td>
                            <td>{{ props.item.leaveName }}</td>
                            <td v-if="!isMobile">{{ props.item.leaveDescription }}</td>
                            <td v-if="!isMobile">{{ props.item.created.createdOn|moment('YYYY-MM-DD') }}</td>
                            <td v-if="!isMobile">System Generated</td>
                        </tr>
                        <tr v-else @click="viewEdit(props.item)" title="Click to Edit" class="clickable">
                            <td>{{ props.item.leaveCode }}</td>
                            <td>{{ props.item.leaveName }}</td>
                            <td v-if="!isMobile">{{ props.item.leaveDescription }}</td>
                            <td v-if="!isMobile">{{ props.item.created.createdOn|moment('YYYY-MM-DD') }}</td>
                            <td v-if="!isMobile">
                                <v-chip small class="user-chip-container">
                                    <v-avatar>
                                        <v-img :src="awsS3Helper.getAvatarUrl(props.item.created.userId)"></v-img>
                                    </v-avatar>
                                    <b>{{props.item.created.firstName}} {{props.item.created.lastName}}</b>
                                </v-chip>
                            </td>
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>

            <!-- Comments and Updates -->
            <v-layout row wrap>
                <v-flex xs12 sm6>
                    <!-- comments history component -->
                    <comments-history v-if="!loading" :comments_data="commentsData"></comments-history>
                </v-flex>
                <v-flex xs12 sm6>
                    <update-history v-if="!loading" ref="updateHistoryRef" :page_query_id="pageQueryId"></update-history>
                </v-flex>
            </v-layout>
        </v-flex>

        <v-dialog v-model="dialogAddEdit" persistent width="500">
            <addedit-leavetype ref="addEditLeaveTypeRef" :dialogAddEdit.sync="dialogAddEdit" @send="getLeaveTypes"></addedit-leavetype>
        </v-dialog>

        <!-- status messaging component -->
        <status-messaging :page_query_id="pageQueryId" @refresh="updateHandler"></status-messaging>
    </v-layout>  
</template>
<script>

import leaveTypesApi from '@/api/leavetypes'
import pageKeys from '@/common/pagekeys'
import notificationTypes from '@/common/notificationTypes'
import awsS3Helper from '@/helpers/aws-s3'
import userPrefs from '@/helpers/user-prefs'
import rowsPerPageItems from '@/constants/rows-per-page'
import { hasAddRights, hasEditRights } from '@/helpers/access-permissions'

export default { 
    name: 'LeaveTypes',
    metaInfo: {
        title: 'LeaveTypes',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'View and create Leave Types' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },    
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
        'addedit-leavetype' : () => import('@/components/LeaveTypes/AddEditLeaveType'),
        'status-messaging': () => import('@/components/Common/StatusMessaging'),
        'update-history': () => import('@/components/Common/UpdatesHistory'),
        'comments-history': () => import('@/components/Common/CommentsHistory')
    },   
    data () {
        const routeName = this.$route.name
        const savedSort = userPrefs.getSort(routeName)

        return {
            awsS3Helper,
            rowsPerPageItems,
            paginationSync: {
                rowsPerPage: userPrefs.getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            search: null,
            error: false,
            errorMessage: '',
            loading: false,
            disabled: false,
            userDefined: false,
            dialogAddEdit: false,
            mode: '',
            headers: [
                {text:'Code', align: 'left', value:'leaveCode', width: '10%', sortable:true},
                {text:'Name', align: 'left', value:'leaveName', width: '20%', sortable:true},
                {text:'Description', align: 'left',value: 'leaveDescription', width: '25%', sortable:true},
                {text:'Created On', align: 'left',value: 'createdOn', width: '20%', sortable:true},
                {text:'Created By', align: 'left',value: '', width: '25%', sortable:false},
            ],
            headers_mobile: [
                {text:'Code', align: 'left', value:'leaveCode', width: '10%', sortable:true},
                {text:'Name', align: 'left', value:'leaveName', width: '20%', sortable:true},
            ],
            leave_types: []
        }
    },
    methods: {   
        updateHandler(data) {
            if (data!=this.$store.state.userId) {
                this.getLeaveTypes(false)
            }
            //invoke updateHistoryRef appendData 
            this.$refs.updateHistoryRef.getHistory()
        },     
        viewEdit(data) {
            if (this.hasEditRights) {
                this.$refs.addEditLeaveTypeRef.setParamsEdit(data)
                this.dialogAddEdit = true
            }
        },
        showAdd() {
            this.$refs.addEditLeaveTypeRef.setParamsAdd()
            this.dialogAddEdit = true
        },  
        async getLeaveTypes(show_loading) {
            try{
                this.loading = show_loading
                const response = await leaveTypesApi.getByCompany()
                if (response&&response.status==200){
                    this.leave_types=response.data.items
                }
                this.loading = false
            }
            catch(error){
                this.error = true
                this.errorMessage = error
            }
        }
    },
    created() {
        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'Leave Types'
        })

        this.getLeaveTypes(true)
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            userPrefs.updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            userPrefs.updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            userPrefs.updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        }
    },
    computed: {
        hasAddRights() {
            return hasAddRights(this.$router.currentRoute.path)
        },
        hasEditRights() {
            return hasEditRights(this.$router.currentRoute.path)
        },
        isMobile() {
            return window.innerWidth < 600
        },
        companyIdStr() {
            return this.$store.state.companyId
        },
        pageQueryId() {
            return `${this.companyIdStr}_leavetypes`
        },
        commentsData() {
            if (!this.loading) {
                return {
                    pageQueryId: this.pageQueryId,
                    pageItemCreatedBy: null,
                    pageLink: this.$router.currentRoute.path,
                    pageKey: pageKeys.leave_types,
                    notificationTypeId: notificationTypes.leave_types
                }
            }
        },
        leave_types_filtered() {
            var temp = this.leave_types

            //search handler
            if (this.userDefined) {
                temp = temp.filter(item => item.created.userId != 0)
            }

            if (this.search) {
                const searchLower = this.search.toLowerCase();

                temp = temp.filter(item => {
                    const leaveCode = item.leaveCode.toLowerCase().includes(searchLower)
                    const leaveName = item.leaveName.toLowerCase().includes(searchLower)
                    const leaveDescription = item.leaveDescription.toLowerCase().includes(searchLower)
                  
                    return leaveCode || leaveName || leaveDescription
                });
            }
            return temp
        }
    }
  }
</script>